<script setup lang="ts">
import { computed } from 'vue'
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectPortal,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
  SelectItemIndicator,
  SelectScrollUpButton,
  SelectScrollDownButton,
} from 'radix-vue'

interface Option {
  label: string
  value: string
}

const props = defineProps<{
  modelValue?: string
  options: readonly (Option | string)[]
  placeholder?: string
  disabled?: boolean
  triggerClass?: string
  iconClass?: string
}>()

defineEmits<{
  'update:modelValue': [value: string]
}>()

const normalizedOptions = computed<Option[]>(() =>
  props.options.map((o) => (typeof o === 'string' ? { label: o, value: o } : o)),
)
</script>

<template>
  <SelectRoot
    :model-value="modelValue"
    :disabled="disabled"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <SelectTrigger
      :class="triggerClass ?? 'flex items-center justify-between w-full bg-surface-container-high text-on-surface font-body text-sm px-3 py-2 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-40 disabled:cursor-not-allowed transition-shadow data-[placeholder]:text-outline/50'"
    >
      <SelectValue :placeholder="placeholder ?? 'Select...'" />
      <span :class="iconClass ?? 'material-symbols-outlined text-outline text-[18px] ml-2 shrink-0'">expand_more</span>
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="z-50 bg-surface-container-high border border-border rounded overflow-hidden min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)] shadow-xl"
        position="popper"
        :side-offset="2"
      >
        <SelectScrollUpButton class="flex items-center justify-center h-6 text-outline bg-surface-container-high">
          <span class="material-symbols-outlined text-[16px]">expand_less</span>
        </SelectScrollUpButton>

        <SelectViewport class="p-1">
          <SelectItem
            v-for="opt in normalizedOptions"
            :key="opt.value"
            :value="opt.value"
            class="flex items-center justify-between px-3 py-2 font-mono text-xs text-on-surface hover:bg-surface-container-highest focus:bg-surface-container-highest cursor-pointer focus:outline-none data-[highlighted]:bg-surface-container-highest data-[state=checked]:text-primary"
          >
            <SelectItemText>{{ opt.label }}</SelectItemText>
            <SelectItemIndicator>
              <span class="material-symbols-outlined text-primary text-[16px]">check</span>
            </SelectItemIndicator>
          </SelectItem>
        </SelectViewport>

        <SelectScrollDownButton class="flex items-center justify-center h-6 text-outline bg-surface-container-high">
          <span class="material-symbols-outlined text-[16px]">expand_more</span>
        </SelectScrollDownButton>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
